/**
 * Cattura e persistenza dell'attribuzione marketing (first-touch).
 *
 * All'atterraggio legge utm_*, gclid, wbraid, gbraid dalla URL e li salva in
 * localStorage (TTL 90 giorni). First-touch: una attribuzione già salvata non
 * viene sovrascritta, TRANNE quando arriva un nuovo click id Google Ads (un
 * nuovo clic pagato deve vincere su una vecchia visita organica).
 *
 * Nessun consenso richiesto per la CATTURA: i dati restano nel browser
 * dell'utente e vengono inviati solo insieme a un form che l'utente sceglie
 * di inviare (base giuridica: esecuzione della richiesta). Nessun dato va a
 * Google da qui — il tracciamento Google resta gestito da Consent Mode V2.
 */

import { type Attribution } from "@shared/marketing";
import { reportLanding } from "@shared/organic-report";

const KEY = "abmark_attribution_v1";
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface Stored {
  a: Attribution;
  savedAt: number;
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.a || typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

const PARAMS = [
  ["utm_source", "utmSource"],
  ["utm_medium", "utmMedium"],
  ["utm_campaign", "utmCampaign"],
  ["utm_term", "utmTerm"],
  ["utm_content", "utmContent"],
  ["gclid", "gclid"],
  ["wbraid", "wbraid"],
  ["gbraid", "gbraid"],
] as const;

/** Chiamata una volta al boot dell'app (main.tsx). */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const incoming: Attribution = {};
    let hasAny = false;
    for (const [param, field] of PARAMS) {
      const v = url.searchParams.get(param);
      if (v && v.trim()) {
        (incoming as Record<string, string>)[field] = v.trim().slice(0, 200);
        hasAny = true;
      }
    }
    // Search referrers without UTM tags must still be attributable. Never
    // persist query strings (which may contain personal data) in the landing.
    if (document.referrer) {
      try {
        const ref = new URL(document.referrer);
        if (ref.origin !== url.origin) {
          incoming.referrer = ref.origin;
          hasAny = true;
        }
      } catch { /* malformed referrer */ }
    }
    if (!hasAny) return;
    incoming.landingPage = reportLanding(url.pathname);
    incoming.firstTouchAt = new Date().toISOString();

    const existing = read();
    const incomingHasClickId = !!(incoming.gclid || incoming.wbraid || incoming.gbraid);
    // First-touch: non sovrascrivere, salvo nuovo clic Google Ads.
    if (existing && !incomingHasClickId) return;
    localStorage.setItem(KEY, JSON.stringify({ a: incoming, savedAt: Date.now() }));
  } catch {
    // localStorage non disponibile (private mode ecc.): nessun errore visibile.
  }
}

/** Attribuzione da allegare ai form lead; null se nessuna. */
export function getAttribution(): Attribution | null {
  return read()?.a ?? null;
}
